import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { AmountFormatCell } from "components/common/formatter";
import { useActiveExpenses } from "services/expenseService";
import { GridLoader } from "components/GridLoader";
import styles from "./Common.module.scss";

const ExpenseSelectionGrid = ({
  gridData,
  dataItemKey,
  onItemChange,
  onSelectAll,
  loading,
}) => {
  const SelectionHeaderCell = () => (
    <div className="d-flex align-items-center justify-content-center">
      <Checkbox
        className={styles.selectcheckbox}
        onChange={(e) => onSelectAll(e.value)}
        value={gridData.length > 0 && gridData.every((item) => item.add)}
      />
    </div>
  );

  return (
    <Grid
      data={gridData}
      dataItemKey={dataItemKey}
      className="grid-header-sm"
      style={{ height: '100%' }}
      showLoader={loading}
      loader={
        <div style={{ height: "50px" }}>
          <GridLoader />
        </div>
      }
    >
      <Column
        field="add"
        title=" "
        width="90px"
        headerCell={SelectionHeaderCell}
        cell={(cellProps) => (
          <td className="text-center align-middle">
            <Checkbox
              className={styles.selectcheckbox}
              value={cellProps.dataItem.add === true}
              onChange={(e) => onItemChange(cellProps.dataItem, e.value)}
            />
          </td>
        )}
      />
      <Column field="details" title="Details" />
      <Column field="nominalRef" title="Nominal" width="100px" />
      <Column
        field="netAmount"
        title="Value"
        width="100px"
        cell={AmountFormatCell}
        headerClassName="grid-header-center"
      />
    </Grid>
  );
};

const ExpenseSelectionDialog = ({
  open,
  onClose,
  onAdd,
  title,
  gridData,
  dataItemKey,
  onItemChange,
  onSelectAll,
  loading = false,
}) => {
  const selectedCount = gridData.filter((item) => item.add === true).length;

  const handleAdd = () => {
    const selected = gridData.filter((item) => item.add === true);
    if (!selected.length) return;
    onAdd(selected);
  };

  if (!open) return null;

  return (
    <Dialog title={title} onClose={onClose} className="dialog-md">
      <div className="d-flex flex-column h-100 px-4 pt-3 pb-2" style={{ minHeight: 0 }}>
        <ExpenseSelectionGrid
          gridData={gridData}
          dataItemKey={dataItemKey}
          onItemChange={onItemChange}
          onSelectAll={onSelectAll}
          loading={loading}
        />
      </div>
      <DialogActionsBar>
        <div className="dialog-footer w-100 d-flex justify-content-end">
          <Button
            className="common-btn bgBtnSecondary border-0 me-3"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="common-btn me-3"
            disabled={selectedCount === 0 || loading}
            onClick={handleAdd}
          >
            Add
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export const SelectExpenseDialog = ({ open, onClose, onAdd }) => {
  const { data: activeExpenses = [], loading } = useActiveExpenses();
  const [selections, setSelections] = useState({});

  useEffect(() => {
    if (!open) {
      setSelections({});
    }
  }, [open]);

  const gridData = useMemo(() => {
    if (!open) return [];
    return activeExpenses.map((item) => ({
      ...item,
      add: selections[item.expenseId] ?? false,
    }));
  }, [open, activeExpenses, selections]);

  const handleItemChange = (dataItem, value) => {
    setSelections((prev) => ({
      ...prev,
      [dataItem.expenseId]: value,
    }));
  };

  const handleSelectAll = (value) => {
    setSelections((prev) => {
      const next = { ...prev };
      activeExpenses.forEach((item) => {
        next[item.expenseId] = value;
      });
      return next;
    });
  };

  return (
    <ExpenseSelectionDialog
      open={open}
      onClose={onClose}
      onAdd={onAdd}
      title="Select Expense"
      gridData={gridData}
      dataItemKey="expenseId"
      onItemChange={handleItemChange}
      onSelectAll={handleSelectAll}
      loading={loading}
    />
  );
};


